import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plane, Plus, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = { vacation: "Vacation", sick: "Sick", personal: "Personal", other: "Other" };
const STATUS_CLS = { pending: "bg-[#ffc70020] text-[#ffc700] border-[#ffc70040]", approved: "bg-[#ff000022] text-[color:var(--accent)] border-[#ff000044]", rejected: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]" };

export default function TimeOff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/timeoff").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const decide = async (id, status) => { await api.patch(`/timeoff/${id}`, { status }); load(); toast.success(`Request ${status}`); };
  const cancel = async (id) => { if (!window.confirm("Cancel this request?")) return; await api.delete(`/timeoff/${id}`); load(); };
  return (
    <div className="space-y-6" data-testid="timeoff-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">HR</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Time Off</h1>
          <p className="text-sm text-slate-400 mt-1">Request days off and see the status of every request.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-timeoff" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}>
          <Plus size={16} /> Request time off
        </button>
      </div>
      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0b0d12] text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-3">Who</th><th className="text-left p-3">Type</th><th className="text-left p-3">Dates</th><th className="text-left p-3">Days</th><th className="text-left p-3">Status</th><th className="text-left p-3">Decided by</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800" data-testid={`to-${r.id}`}>
                <td className="p-3 text-slate-200">{r.user_name}</td>
                <td className="p-3 text-slate-300">{TYPES[r.type] || r.type}</td>
                <td className="p-3 text-slate-300">{r.start_date} → {r.end_date}</td>
                <td className="p-3 text-slate-300">{r.days}</td>
                <td className="p-3"><span className={`pill border ${STATUS_CLS[r.status]}`}>{r.status}</span></td>
                <td className="p-3 text-slate-400 text-xs">{r.decided_by_name || "—"}</td>
                <td className="p-3 text-right space-x-1">
                  {canManage && r.status === "pending" && (<>
                    <button onClick={() => decide(r.id, "approved")} data-testid={`to-approve-${r.id}`} className="p-1.5 rounded hover:bg-[#ff000022] text-[color:var(--accent)]"><Check size={14} /></button>
                    <button onClick={() => decide(r.id, "rejected")} data-testid={`to-reject-${r.id}`} className="p-1.5 rounded hover:bg-[#ff3b5c20] text-[#ff3b5c]"><X size={14} /></button>
                  </>)}
                  {(r.user_id === user.id || canManage) && r.status === "pending" && (
                    <button onClick={() => cancel(r.id)} data-testid={`to-cancel-${r.id}`} className="p-1.5 rounded hover:bg-slate-800 text-slate-500"><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500"><Plane size={20} className="inline mr-2" /> No requests yet</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <NewModal onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewModal({ onClose, onDone }) {
  const [f, setF] = useState({ type: "vacation", start_date: "", end_date: "", reason: "" });
  const submit = async (e) => { e.preventDefault(); await api.post("/timeoff", f); onDone(); toast.success("Request submitted"); };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="to-modal">
        <h2 className="text-xl font-bold mb-4">Request time off</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Type</div>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="to-type" className={inp}>{Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label><div className="text-xs uppercase text-slate-500 mb-1">Start</div><input type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="to-start" className={inp} /></label>
          <label><div className="text-xs uppercase text-slate-500 mb-1">End</div><input type="date" required value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="to-end" className={inp} /></label>
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Reason (optional)</div><textarea rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="to-reason" className={inp + " resize-none"} /></label>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button>
          <button type="submit" data-testid="to-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Submit</button>
        </div>
      </form>
    </div>
  );
}
